import {
  TextInput,
  SimpleForm,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  minValue,
  ImageInput,
  SelectInput,
  maxLength,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectArrayInput,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import MuiTextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { RichTextInput } from 'ra-input-rich-text'

import GridContainer from '../../components/GridContainer'
import BookToolbar from '../../components/BookToolbar'
import CustomImageField from '../../components/CustomImageField'
import { validateRequired, validateUnique } from '../../utils/validate'
import { COLLECTION_ITEM_DEFAULT_PRICE } from '../../components/constants'

import CollectionItemsInput from './CollectionItemsInput'

export const getDefaultCollectionPrice = (items = []) =>
  items.reduce(
    (total, item) => total + (COLLECTION_ITEM_DEFAULT_PRICE[item?.type] || 0),
    0,
  )

const CollectionLinks = () => {
  const slug = useWatch({ name: 'slug' })

  const webLink = slug ? `https://fonos.vn/bo-suu-tap/${slug}` : ''
  const deeplink = slug ? `fonos://?type=collection&slug=${slug}` : ''

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary">
          Links
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <MuiTextField
          label="Web link"
          value={webLink}
          fullWidth
          variant="standard"
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <MuiTextField
          label="Deeplink"
          value={deeplink}
          fullWidth
          variant="standard"
          disabled
        />
      </Grid>
    </>
  )
}

const CollectionForm = props => {
  const TYPES = [
    { id: 'visual_image', name: 'Visual image' },
    { id: 'moving_item', name: 'Moving items' },
    { id: 'featured_item', name: 'Featured item' },
    { id: 'live_event', name: 'Live Event' },
  ]

  const DISTRIBUTION_TYPES = [
    { id: 'homepage', name: 'Homepage' },
    { id: 'podcourse', name: 'Podcourse' },
    { id: 'kid_tab', name: 'Kid Tab' },
  ]

  return (
    <SimpleForm {...props} toolbar={<BookToolbar />} redirect="list">
      <GridContainer>
        <Grid item xs={4}>
          <SelectInput
            source="type"
            choices={TYPES}
            fullWidth
            variant="standard"
            required
            validate={validateRequired('Type')}
          />
        </Grid>
        <Grid item xs={8}>
          <TextInput source="name" fullWidth variant="standard" />
        </Grid>
        <Grid item xs={4}>
          <SelectArrayInput
            source="distributions"
            choices={DISTRIBUTION_TYPES}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput source="subtext" fullWidth variant="standard" />
        </Grid>
        <Grid item xs={4}>
          <NumberInput
            source="price"
            fullWidth
            variant="standard"
            label="Price (for web)"
            helperText="Leave blank to fallback to the default price (audiobooks x 129,000đ + podcourses x 449,000đ)"
            validate={[minValue(0)]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">VND</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ReferenceArrayInput
            label="Categories"
            source="categoryIds"
            filter={{
              type: 'book',
              primary: true,
            }}
            reference="categories">
            <AutocompleteArrayInput
              optionText="name"
              fullWidth
              required
              variant="standard"
              filterToQuery={searchText => ({
                name: { $iLike: `%${searchText}%` },
                type: 'book',
                primary: true,
              })}
            />
          </ReferenceArrayInput>
        </Grid>
        <Grid item xs={6}>
          <TextInput
            source="slug"
            label="Slug (for web)"
            fullWidth
            variant="standard"
            validate={[
              validateRequired('Slug'),
              maxLength(255, 'Do not enter more than 255 characters'),
            ]}
          />
        </Grid>
        <CollectionLinks />
        <Grid item xs={12}>
          <RichTextInput
            source="description"
            resettable
            rows={5}
            fullWidth
            variant="standard"
            multiline
          />
        </Grid>
        <Grid item xs={12}>
          <ImageInput
            source="bannerImageUrl"
            label="Banner image"
            accept="image/*"
            multiple={false}
            validate={validateRequired('Banner Image')}>
            <CustomImageField source="src" title="Banner image" />
          </ImageInput>
        </Grid>
        <Grid item xs={12}>
          <CollectionItemsInput source="items" />
        </Grid>

        <Grid item xs={12}>
          <ArrayInput
            label="Quotes"
            source="quotes"
            fullWidth
            validate={[validateUnique('order', true)]}>
            <SimpleFormIterator>
              <NumberInput
                source="order"
                label="Order"
                variant="standard"
                fullWidth
              />
              <ImageInput
                source="avatar"
                label="Avatar"
                accept="image/*"
                multiple={false}
                validate={validateRequired('Avatar')}>
                <CustomImageField source="src" title="Avatar" />
              </ImageInput>
              <TextInput
                source="name"
                label="Name"
                fullWidth
                variant="standard"
                validate={validateRequired('Name')}
              />
              <TextInput
                source="title"
                label="Title"
                fullWidth
                variant="standard"
              />
              <TextInput
                source="quote"
                label="Quote"
                resettable
                rows={4}
                fullWidth
                variant="standard"
                multiline
                validate={[
                  validateRequired('Quote'),
                  maxLength(255, 'Do not enter more than 255 characters'),
                ]}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </GridContainer>
    </SimpleForm>
  )
}

export default CollectionForm
